import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import * as Sentry from '@sentry/react';

import { AuthService } from "../../services/auth.service";
import { stopAuthenticatedReplay } from "../../services/sentry-replay.service";
import { store } from "../../store";

// Maximum number of refresh attempts before giving up
const MAX_REFRESH_ATTEMPTS = 3;
// Cooldown period between refresh attempts (in ms)
const REFRESH_COOLDOWN_MS = 5000;

// PermissionsChangedListener listens for 403 errors and refreshes user permissions
export const PermissionsChangedListener: React.FC = () => {
    const navigate = useNavigate();
    const isRefreshing = useRef(false);
    const attemptCount = useRef(0);
    const lastAttemptTime = useRef(0);
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const handleLogout = () => {
        setShowLogoutDialog(false);
        store.dispatch.auth.logout();
        Sentry.setUser(null);
        stopAuthenticatedReplay();
        localStorage.removeItem('pom-admin-core-persistor');
        navigate("/");
    };

    useEffect(() => {
        const handlePermissionsChanged = async () => {
            const now = Date.now();

            // Prevent multiple simultaneous refreshes
            if (isRefreshing.current) return;

            // Check if we've exceeded max attempts
            if (attemptCount.current >= MAX_REFRESH_ATTEMPTS) {
                // Show logout dialog instead of continuing to retry
                setShowLogoutDialog(true);
                return;
            }

            // Enforce cooldown between attempts
            if (now - lastAttemptTime.current < REFRESH_COOLDOWN_MS) {
                return;
            }

            isRefreshing.current = true;
            lastAttemptTime.current = now;
            attemptCount.current += 1;

            try {
                // Refresh user data and permissions
                await AuthService.whoIam();

                // Success - reset attempt counter
                attemptCount.current = 0;

                // Notify the user
                toast("Your permissions have been updated", {
                    type: "info",
                    autoClose: 3000,
                });

                // Redirect to dashboard (they may no longer have access to current page)
                navigate("/");
            } catch (error) {
                console.error("Failed to refresh permissions:", error);
                
                // If we've now hit max attempts, show the logout dialog
                if (attemptCount.current >= MAX_REFRESH_ATTEMPTS) {
                    setShowLogoutDialog(true);
                }
            } finally {
                isRefreshing.current = false;
            }
        };

        window.addEventListener("permissionsChanged", handlePermissionsChanged);

        return () => {
            window.removeEventListener("permissionsChanged", handlePermissionsChanged);
        };
    }, [navigate]);

    return (
        <Dialog open={showLogoutDialog} onClose={() => {}}>
            <DialogTitle>Session Error</DialogTitle>
            <DialogContent>
                <Typography>
                    We're having trouble verifying your permissions. Please log out and log back in to continue.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleLogout} variant="contained" color="primary">
                    Log Out
                </Button>
            </DialogActions>
        </Dialog>
    );
};
